import { trackVideoSlidesEvent, VideoSlidesEvents } from 'Analytics/video-slides-events';
import type { SlidesCloseDetail, SlidesOpenDetail } from 'Modules/video/components/vm-video-slides/vm-video-slides';

/** The Video.js player methods the coordinator uses. */
export interface SlidesPlayer {
    paused(): boolean;
    ended(): boolean;
    pause(): void;
    play(): Promise<void> | undefined;
    muted(muted: boolean): void;
    currentTime(): number;
}

/**
 * Pauses the video while the slides viewer is open.
 * On close, playback resumes if
 *  - the video was playing,
 *  - the viewer was deep-linked, or
 *  - the user chose "Watch full video".
 *
 * The position is never changed.
 *
 * Also tracks the open and close analytics, which need the player state.
 *
 * @param {Promise} playerReady - the player promise; a deep-linked viewer can open before the player is ready.
 * @returns {function (): void} - a function that detaches the coordinator.
 */
export function createSlidesPlayerCoordinator(playerReady: Promise<SlidesPlayer>): () => void {
    let player: SlidesPlayer | undefined;
    let resumeOnClose = false;

    void playerReady.then((ready) => {
        player = ready;
    });

    const onOpen = ({ detail }: CustomEvent<SlidesOpenDetail>) => {
        const wasPlaying = !!player && !player.paused() && !player.ended();
        if (wasPlaying) player?.pause();
        resumeOnClose = wasPlaying || detail.source === 'deeplink';

        trackVideoSlidesEvent(VideoSlidesEvents.MODAL_OPEN, {
            source: detail.source,
            slide_count: detail.slideCount,
            video_was_playing: wasPlaying,
            video_position_s: player ? Math.round(player.currentTime()) : null,
        });
    };

    const onClose = ({ detail }: CustomEvent<SlidesCloseDetail>) => {
        const track = (videoResumed: boolean) =>
            trackVideoSlidesEvent(VideoSlidesEvents.MODAL_CLOSE, {
                method: detail.method,
                duration_ms: detail.durationMs,
                slides_viewed: detail.slidesViewed,
                max_slide_number: detail.maxSlideNumber,
                last_slide_number: detail.lastSlideNumber,
                video_resumed: videoResumed,
            });

        if (resumeOnClose || detail.method === 'watch_full_video') {
            // Tracked once playback starts or fails, which can wait for the player.
            void playerReady
                .then(play)
                .catch(() => false)
                .then(track);
        } else {
            track(false);
        }
    };

    document.addEventListener('vm-video-slides:open', onOpen);
    document.addEventListener('vm-video-slides:close', onClose);

    return () => {
        document.removeEventListener('vm-video-slides:open', onOpen);
        document.removeEventListener('vm-video-slides:close', onClose);
    };
}

/** Resolves to whether playback started. */
async function play(player: SlidesPlayer): Promise<boolean> {
    try {
        await player.play();
        return true;
    } catch {
        // The autoplay policy can still block unmuted playback; muted playback is usually allowed.
    }
    player.muted(true);
    try {
        await player.play();
        return true;
    } catch {
        return false;
    }
}
